import { baseURL } from '../baseUrl'

export interface SessionData {
  id: string
  status: 'created' | 'submitted' | 'complete'
  created_at: string
  updated_at: string
}

export interface FormData {
  id: string
  partner_subdomain: string
  settings: {
    content_json: string
    layout?: {
      show_pagination?: boolean
      contianer_styles: string
      body_styles: string
      fields?: string
      theme?: string
    }
    primary_scheme_identifier?: string
  }
  validation_scheme: Record<string, any>
  created_at: string
  updated_at: string
}

export interface CreateSessionResponse {
  data: {
    form: FormData
    session: SessionData
  }
}

export interface GetSessionResponse {
  data: {
    form: FormData
    session: SessionData & {
      values?: Record<string, any>
    }
    /** Последние значения, сохраненные через partial submit */
    lastPartialSubmitValues?: Record<string, any>
  }
}

export interface PartialSubmitResponse {
  data: {
    success: boolean
  }
}

export interface SubmitSessionResponse {
  data: Record<string, any>
}

/**
 * Создание новой сессии для формы
 */
export async function createSession(
  formId: string
): Promise<CreateSessionResponse> {
  const response = await fetch(`${baseURL}/form/${formId}/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to create session: ${response.status}`)
  }

  return response.json()
}

/**
 * Получение данных существующей сессии
 */
export async function getSession(
  formId: string,
  sessionId: string
): Promise<GetSessionResponse> {
  const response = await fetch(
    `${baseURL}/form/${formId}/session/${sessionId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to get session: ${response.status}`)
  }

  return response.json()
}

/**
 * Частичное сохранение данных формы (автосохранение)
 */
export async function partialSubmit(
  formId: string,
  sessionId: string,
  values: Record<string, any>
): Promise<PartialSubmitResponse> {
  const response = await fetch(
    `${baseURL}/form/${formId}/session/${sessionId}/submit-partial`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ values }),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to partial submit: ${response.status}`)
  }

  return response.json()
}

/**
 * Финальная отправка формы через сессию
 */
export async function submitSession(
  formId: string,
  sessionId: string,
  values: Record<string, any>
): Promise<SubmitSessionResponse> {
  const response = await fetch(
    `${baseURL}/form/${formId}/session/${sessionId}/submit`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ values }),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to submit session: ${response.status}`)
  }

  return response.json()
}

// ============ LocalStorage helpers ============

const SESSION_STORAGE_PREFIX = 'form_session_'

export interface StoredSession {
  sessionId: string
  createdAt: string
}

/**
 * Сохранить sessionId в localStorage
 */
export function saveSessionToStorage(formId: string, sessionId: string): void {
  const key = `${SESSION_STORAGE_PREFIX}${formId}`
  const data: StoredSession = {
    sessionId,
    createdAt: new Date().toISOString(),
  }
  localStorage.setItem(key, JSON.stringify(data))
}

/**
 * Получить сохраненную сессию из localStorage
 */
export function getSessionFromStorage(formId: string): StoredSession | null {
  const key = `${SESSION_STORAGE_PREFIX}${formId}`
  const stored = localStorage.getItem(key)

  if (!stored) {
    return null
  }

  try {
    return JSON.parse(stored) as StoredSession
  } catch {
    return null
  }
}

/**
 * Удалить сессию из localStorage (после успешного submit)
 */
export function removeSessionFromStorage(formId: string): void {
  const key = `${SESSION_STORAGE_PREFIX}${formId}`
  localStorage.removeItem(key)
}
